"use client";
import React, { useState } from "react";
import { BookOpen, Clock } from "lucide-react";
import { AnimatePresence } from "framer-motion";
import * as motion from "motion/react-client";
import { TEducationConfig } from "@/app/config";

// Helper function to convert YouTube URL to embed URL
const getYouTubeEmbedUrl = (url: string) => {
  if (!url) return "";

  // Handle different YouTube URL formats
  let videoId = "";

  if (url.includes("youtube.com/watch?v=")) {
    videoId = url.split("v=")[1]?.split("&")[0];
  } else if (url.includes("youtu.be/")) {
    videoId = url.split("youtu.be/")[1]?.split("?")[0];
  } else if (url.includes("youtube.com/embed/")) {
    // Already an embed URL
    return url;
  }

  return videoId ? `https://www.youtube.com/embed/${videoId}` : "";
};

const CourseListSection = ({
  config,
}: {
  config: TEducationConfig["CourseListSection"];
}) => {
  const [selectedFilter, setSelectedFilter] = useState("See All");

  const filteredCourses =
    selectedFilter === "See All"
      ? config.courses
      : config.courses.filter((course) => course.subject === selectedFilter);

  // Container variants for staggered animation
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  return (
    <div id="course-list" className="bg-white py-16 px-6">
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="mb-12">
          {/* Course List Tag */}
          <motion.div
            className="mb-8"
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <span className="underline-style text-black font-medium text-base">
              COURSE LIST
            </span>
          </motion.div>

          {/* Title and Filters */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            {/* Left Side - Title */}
            <motion.h2
              className="text-3xl lg:text-4xl font-bold text-gray-900"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              {config.title}
            </motion.h2>

            {/* Right Side - Filter Selection */}
            <motion.div
              className="flex flex-wrap gap-2"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              {config.filters.map((filter, index) => (
                <motion.button
                  key={filter}
                  onClick={() => setSelectedFilter(filter)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    selectedFilter === filter
                      ? "bg-gray-900 text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.5 + index * 0.1 }}
                  whileHover={{
                    scale: 1.05,
                    y: -2,
                  }}
                  whileTap={{ scale: 0.95 }}
                >
                  {filter}
                </motion.button>
              ))}
            </motion.div>
          </div>
        </div>

        {/* Course Cards Grid */}
        <AnimatePresence mode="wait">
          <motion.div
            key={selectedFilter} // This ensures re-animation when filter changes
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
          >
            {filteredCourses.map((course, index) => {
              const embedUrl = getYouTubeEmbedUrl(course?.videoUrl);

              return (
                <motion.div
                  key={course.id}
                  className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow border border-gray-100 overflow-hidden"
                  variants={{
                    hidden: {
                      opacity: 0,
                      y: 30,
                      scale: 0.95,
                    },
                    visible: {
                      opacity: 1,
                      y: 0,
                      scale: 1,
                      transition: {
                        duration: 0.5,
                        ease: "easeOut",
                      },
                    },
                    exit: {
                      opacity: 0,
                      scale: 0.95,
                      y: -20,
                      transition: {
                        duration: 0.3,
                      },
                    },
                  }}
                  whileHover={{
                    y: -8,
                    scale: 1.02,
                    boxShadow: "0 20px 40px rgba(0, 0, 0, 0.1)",
                  }}
                  transition={{ type: "spring", stiffness: 300, damping: 20 }}
                  layout
                >
                  {/* Course Video with Subject Tag */}
                  <div className="relative h-48 bg-gray-100 overflow-hidden">
                    {embedUrl ? (
                      <iframe
                        loading="lazy"
                        height="192"
                        src={embedUrl}
                        title="YouTube video player"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        referrerPolicy="strict-origin-when-cross-origin"
                        allowFullScreen
                        className="w-full h-full rounded-none"
                      />
                    ) : (
                      <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                        <p className="text-gray-500 text-sm">
                          Video not available
                        </p>
                      </div>
                    )}

                    {/* Subject Tag */}
                    <motion.div
                      className="absolute top-3 left-3"
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
                      whileHover={{ scale: 1.1 }}
                    >
                      <span
                        className={`${course.color} text-white px-2 py-1 rounded text-xs font-medium`}
                      >
                        {course.subject}
                      </span>
                    </motion.div>
                  </div>

                  {/* Course Content */}
                  <motion.div
                    className="p-6"
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.4 + index * 0.1 }}
                  >
                    {/* Course Stats */}
                    <motion.div
                      className="flex items-center gap-4 mb-4 text-sm text-gray-600"
                      initial={{ opacity: 0, y: 10 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: 0.5 + index * 0.1 }}
                    >
                      <motion.div
                        className="flex items-center gap-1"
                        whileHover={{ scale: 1.05 }}
                      >
                        <BookOpen className="w-4 h-4" />
                        <span>{course.lessons} Lessons</span>
                      </motion.div>
                      <motion.div
                        className="flex items-center gap-1"
                        whileHover={{ scale: 1.05 }}
                      >
                        <Clock className="w-4 h-4" />
                        <span>{course.duration}</span>
                      </motion.div>
                    </motion.div>

                    {/* Course Title */}
                    <motion.h3
                      className="text-lg font-semibold text-gray-900 leading-tight"
                      initial={{ opacity: 0, y: 10 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: 0.6 + index * 0.1 }}
                      whileHover={{ color: "#002C43" }}
                    >
                      {course.title}
                    </motion.h3>
                  </motion.div>
                </motion.div>
              );
            })}
          </motion.div>
        </AnimatePresence>

        {/* Show message if no courses found */}
        <AnimatePresence>
          {filteredCourses.length === 0 && (
            <motion.div
              className="text-center py-12"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ duration: 0.5 }}
            >
              <motion.p
                className="text-gray-600 text-lg"
                initial={{ y: 20 }}
                animate={{ y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                No courses found for {selectedFilter}
              </motion.p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default CourseListSection;
